import { FormEvent, useState } from 'react';
import type { User, FamilyRole, RecurrenceRule } from '@kinapp/shared';
import { api } from '../lib/api';
import { FormField } from './FormField';
import { AddressAutocomplete } from './AddressAutocomplete';
import { AppointmentMap } from './AppointmentMap';
import { AttendeeSelect, AttendeeState } from './AttendeeSelect';
import { RecurrenceSelect } from './RecurrenceSelect';

interface CreateAppointmentFormProps {
  familyId: string;
  dependents: Array<{ id: string; name: string }>;
  familyMembers: Array<{ user: User; role: FamilyRole }>;
  onSuccess: () => void;
  onCancel: () => void;
  initialDateTime?: string;
}

export function CreateAppointmentForm({
  familyId,
  dependents,
  familyMembers,
  onSuccess,
  onCancel,
  initialDateTime,
}: CreateAppointmentFormProps) {
  const [title, setTitle] = useState('');
  const [dateTime, setDateTime] = useState(initialDateTime ?? '');
  const [location, setLocation] = useState('');
  const [coordinates, setCoordinates] = useState<{ lat: number; lng: number } | null>(null);
  const [notes, setNotes] = useState('');
  const [attendees, setAttendees] = useState<AttendeeState>({
    selectedUserIds: [],
    selectedDepIds: dependents.map((d) => d.id),
    externalGuests: [],
  });
  const [recurrenceRule, setRecurrenceRule] = useState<RecurrenceRule | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Extract date portion for smart recurrence labels
  const refDate = dateTime ? dateTime.slice(0, 10) : undefined;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    if (!dateTime) {
      setError('Date and time is required');
      return;
    }

    setLoading(true);

    try {
      await api.createAppointment({
        familyId,
        dependentId: attendees.selectedDepIds[0],
        dependentIds: attendees.selectedDepIds,
        title: title.trim(),
        dateTime,
        location: location.trim() || undefined,
        locationLat: coordinates?.lat,
        locationLng: coordinates?.lng,
        attendingUserIds: attendees.selectedUserIds,
        externalGuests: attendees.externalGuests.length ? attendees.externalGuests : undefined,
        recurrenceRule: recurrenceRule ?? undefined,
        notes: notes.trim() || undefined,
      });

      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to create appointment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && (
        <div className="rounded-md bg-red-50 p-4 mb-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <AttendeeSelect
        dependents={dependents}
        familyMembers={familyMembers}
        value={attendees}
        onChange={setAttendees}
      />

      <FormField label="Title" id="appointment-title" required>
        <input
          type="text"
          id="appointment-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="input"
          placeholder="e.g., Doctor's appointment"
          required
        />
      </FormField>

      <FormField label="Date and time" id="appointment-datetime" required>
        <input
          type="datetime-local"
          id="appointment-datetime"
          value={dateTime}
          onChange={(e) => setDateTime(e.target.value)}
          min={new Date().toISOString().slice(0, 16)}
          className="input"
          required
        />
      </FormField>

      <FormField label="Recurrence" id="appointment-recurrence">
        <RecurrenceSelect value={recurrenceRule} onChange={setRecurrenceRule} referenceDate={refDate} />
      </FormField>

      <FormField
        label="Location"
        id="appointment-location"
        helpText="Start typing to search for UK addresses"
      >
        <AddressAutocomplete
          value={location}
          onChange={setLocation}
          onSelectLocation={(loc) => {
            setLocation(loc.address);
            setCoordinates({ lat: loc.lat, lng: loc.lng });
          }}
          placeholder="e.g., City Hospital, London"
          id="appointment-location"
        />

        {coordinates && (
          <button
            type="button"
            onClick={() => {
              setCoordinates(null);
            }}
            className="mt-2 text-sm text-gray-600 hover:text-gray-900"
          >
            Clear location and enter manually
          </button>
        )}

        {coordinates && (
          <div className="mt-3">
            <AppointmentMap
              lat={coordinates.lat}
              lng={coordinates.lng}
              locationName={location}
              className="h-48"
            />
          </div>
        )}
      </FormField>

      <FormField label="Notes" id="appointment-notes">
        <textarea
          id="appointment-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="input"
          rows={3}
          placeholder="Additional details..."
        />
      </FormField>

      <div className="flex gap-3 mt-8">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 btn btn-secondary"
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex-1 btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={loading}
        >
          {loading ? 'Creating...' : 'Create appointment'}
        </button>
      </div>
    </form>
  );
}
